/**
 * 點數錢包型別。
 * -----------------------------------------------------------------------------
 * 對齊 `api-service/model/entity/firestore/wallet_entity.py`：
 * - `balance`：總餘額
 * - `reserved`：預扣中（job 進行中尚未結算）
 * - `available = balance - reserved`：實際可動用點數（後端順手計算後一併回傳）
 *
 * 前端只負責顯示與限制使用者操作；任何點數異動最終由後端決定。
 */

/**
 * 後端登入後回傳的錢包資料（snake_case，照搬 backend 欄位）。
 */
export interface WalletDto {
  balance: number;
  reserved: number;
  available: number;
  total_consumed: number;
  total_topped_up: number;
  updated_datetime: string | null;
}

/**
 * 前端使用的錢包型別（camelCase，方便 React／TS 端撰寫）。
 *
 * @example
 *   const wallet: Wallet = { balance: 100, reserved: 10, available: 90, totalConsumed: 0, totalToppedUp: 0 };
 *   wallet.available; // 90
 */
export interface Wallet {
  balance: number;
  reserved: number;
  available: number;
  totalConsumed: number;
  totalToppedUp: number;
  updatedDatetime: string | null;
}

/**
 * `WalletDto` → `Wallet` 的欄位對應。
 *
 * @param dto 後端回傳的原始資料。
 * @returns camelCase 化的 :type:`Wallet`。
 *
 * @example
 *   mapWalletDto({ balance: 100, reserved: 0, available: 100, total_consumed: 0, total_topped_up: 0 });
 *   // → { balance: 100, reserved: 0, available: 100, totalConsumed: 0, totalToppedUp: 0 }
 */
export function mapWalletDto(dto: WalletDto): Wallet {
  return {
    balance: dto.balance,
    reserved: dto.reserved,
    available: dto.available,
    totalConsumed: dto.total_consumed,
    totalToppedUp: dto.total_topped_up,
    updatedDatetime: dto.updated_datetime,
  };
}

// ─── R45 Beta v0.0.1 — per-(user, group) wallet ──────────────────────────────
//
// 對齊 docs/40 §6 + §8 + `api-service/app/wallet/models.py`:
// - Path:`users/{cuid}/wallets/{gid}` doc(per-(user,group) 配對)
// - 切 group 看到不同 balance + reserved + monthly_grant cycle
// - Realtime:訂 doc onSnapshot,任何 reserve / settle / refund / topup / monthly_grant
//   寫入即時推到 FE
//
// 註:交易流水(`users/{cuid}/wallets/{gid}/transactions/{txn_id}`)與 group 側 audit
// (`groups/{gid}/wallet_events/{event_id}`)的型別尚未接 UI,先不在此預先定義;待真做
// 流水 / wallet audit 畫面時再加回(兩個事件 union 共用一份 type)。
// ⚠️ R391 備忘(真做流水畫面時要接):2026-09-25 起 ledger 多第 9 種 `type: "expire"`
// (「點數到期」,`amount` 記正值失效量),且帶效期的 `redeem` 流水多 `lot_expires_at`
// (失效瞬間;顯示「有效至」要減一天取台北日期,或直接用 `/me` 的 `expires_on`)。

// ─── R391 — 兌換點數效期(docs/74 §1;BE 2026-09-25)─────────────────────────────

/** `/me` 殼 `wallet.expiring[]` 單筆:BE 已換算好的「有效至」日期(台北,含當天)。
 * R391.2 起多 `expires_at`(失效瞬間 UTC;分鐘級批次顯示到時分用)。 */
export interface WalletExpiringLotDto {
  amount: number;
  expires_on: string;
  expires_at?: string;
}

/**
 * onSnapshot doc 殼 `expiring_lots[]` 單筆。`expires_at` 是「失效瞬間」(= 最後可用日
 * **隔天** 00:00 台北)—— Firestore SDK 給 Timestamp 物件,REST dump 可能是 ISO 字串,
 * 一律 unknown 進 `lotExpiresOnFromExpiresAt` 換算。
 */
export interface WalletDocExpiringLotDto {
  expires_at?: unknown;
  remaining?: number;
}

/** domain 統一形狀:`amount` = 這批還剩幾點;`expiresOn` = 「有效至」(換算失敗 → null);
 * `expiresAt` = 失效瞬間 ISO(R391.2;顯示形狀交 `pointsExpiryDisplay` 判天級/分鐘級)。
 * `expiresAt` optional 僅為相容既有 fixture,mapper 一律帶值,caller 讀 `?? null`。 */
export interface WalletExpiringLot {
  amount: number;
  expiresOn: string | null;
  expiresAt?: string | null;
}

/**
 * 錢包 DTO — **兩種輸入殼共用一份**(R74 對齊 docs/47):
 *   1. **onSnapshot 完整 doc**(docs/47 §1.B):原始 `users/{cuid}/wallets/{gid}` doc,
 *      帶 `cuid` / `group_id` / `monthly_grant_units` / `version` / `schema_version`,
 *      但**無** `available` / `used` / `grant_per_period`(那些是 FE / `/me` 派生)。
 *   2. **`/me` 輕量殼**(docs/47 §1.A):`{ balance, reserved, available, used,
 *      grant_per_period }`,**無** `cuid` / `group_id` / `monthly_grant_units` /
 *      `version`(那些 doc 才有)。
 *
 * 兩者欄位互補,故除 `balance` / `reserved` 外其餘皆 optional;`mapWalletEntityDto`
 * 以 fallback 鏈把兩種殼都 map 成同一個 `WalletEntity`。
 */
export interface WalletEntityDto {
  cuid?: string;
  group_id?: string;
  balance: number;
  reserved: number;
  /** `/me` 殼直接給(尊重後端值);doc 殼無此欄,mapper 退回 `balance - reserved`。 */
  available?: number;
  /** `/me` 殼:本期已使用(docs/74 §1.A;2026-09-25 起 = `max(0, cap - (balance - T))`,BE 算好)。 */
  used?: number;
  /** `/me` 殼:本期上限 cap(docs/47);doc 殼用 `monthly_grant_units`。 */
  grant_per_period?: number;
  /** doc 殼:本期上限 cap(用量% 分母);`/me` 殼用 `grant_per_period`。 */
  monthly_grant_units?: number;
  /** `/me` 殼:帶效期的兌換點批次(升冪;R391)。缺欄(舊 BE)= 沒有批次。 */
  expiring?: WalletExpiringLotDto[];
  /** doc 殼:帶效期的兌換點批次(升冪;R391)。缺欄 = 沒有批次。 */
  expiring_lots?: WalletDocExpiringLotDto[];
  last_granted_period?: string | null;
  next_grant_due_at?: string | null;
  version?: number;
  schema_version?: 'v0.0.1';
  created_at?: string | null;
  updated_at?: string | null;
}

export interface WalletEntity {
  cuid: string;
  groupId: string;
  balance: number;
  reserved: number;
  /** 目前可動用點數(剩餘):`/me` 殼取後端 `available`,doc 殼派生 `balance - reserved`。 */
  available: number;
  /**
   * 本期已使用:`/me` 殼取後端 `used`(已是扣 T 的新口徑),doc 殼派生
   * `max(0, monthlyGrantUnits - (balance - T))`(R391 對齊 docs/74 §1.C)。
   * mapper 一律帶值;型別 optional 僅為相容既有 fixture(用量% 走 available + cap + T,
   * 不依賴此欄),caller 讀取建議 `wallet.used ?? 0`。
   */
  used?: number;
  /** 本期上限 cap(用量% 分母;試玩工作區 = 0):doc 取 `monthly_grant_units`,`/me` 取 `grant_per_period`。 */
  monthlyGrantUnits: number;
  /**
   * 帶效期的兌換點批次(升冪;R391)。mapper 一律帶值(沒有批次 = []);型別 optional
   * 僅為相容既有 fixture(同 `used` 慣例),caller 讀取一律 `wallet.expiringLots ?? []`。
   */
  expiringLots?: WalletExpiringLot[];
  lastGrantedPeriod: string | null;
  nextGrantDueAt: string | null;
  version: number;
  createdAt: string | null;
  updatedAt: string | null;
}

const TAIPEI_OFFSET_MS = 8 * 60 * 60 * 1000; // 台北無 DST,固定 UTC+8
const DAY_MS = 24 * 60 * 60 * 1000;

/** Firestore Timestamp(toDate / seconds)、ISO 字串、epoch ms → Date;認不得 → null。 */
function instantToDate(raw: unknown): Date | null {
  if (raw == null) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === 'string' || typeof raw === 'number') {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw === 'object') {
    const o = raw as { toDate?: unknown; seconds?: unknown };
    if (typeof o.toDate === 'function') {
      try {
        const d = (o.toDate as () => Date)();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
      } catch {
        return null;
      }
    }
    if (typeof o.seconds === 'number' && Number.isFinite(o.seconds)) {
      return new Date(o.seconds * 1000);
    }
  }
  return null;
}

/**
 * doc 殼 `expiring_lots[].expires_at`(失效瞬間 = 最後可用日**隔天** 00:00 台北)→
 * 「有效至」日期字串(台北,含當天;與 `/me` 的 `expires_on` 同語意)。
 *
 * 換算:instant 平移到 UTC+8 再減一天取日期 —— 例 `2026-11-22T16:00:00Z`
 * (= 11-23 00:00 台北)→ `"2026-11-22"`。認不得的形狀 → null(**不猜日期**;
 * 顯示端對 null 批次不顯到期日,但 `amount` 仍計入用量% 的 T)。
 */
export function lotExpiresOnFromExpiresAt(raw: unknown): string | null {
  const instant = instantToDate(raw);
  if (!instant) return null;
  const shifted = new Date(instant.getTime() + TAIPEI_OFFSET_MS - DAY_MS);
  return shifted.toISOString().slice(0, 10);
}

/** doc 殼 `expires_at`(Timestamp / ISO)→ 統一 ISO 字串(R391.2;給 `pointsExpiryDisplay`
 * 判天級/分鐘級用);認不得 → null。 */
export function lotExpiresAtIso(raw: unknown): string | null {
  return instantToDate(raw)?.toISOString() ?? null;
}

/** 兩殼的批次欄 → 統一 domain 形狀(升冪;剩 0 點的批次略過 —— 對 T 與顯示都是 no-op)。 */
function mapExpiringLots(dto: WalletEntityDto): WalletExpiringLot[] {
  // `/me` 殼優先:BE 已換算好「有效至」;doc 殼由失效瞬間自行換算。缺欄 = 沒有批次。
  const lots: WalletExpiringLot[] = Array.isArray(dto.expiring)
    ? dto.expiring.map((l) => ({
        amount: typeof l?.amount === 'number' && Number.isFinite(l.amount) ? l.amount : 0,
        expiresOn: typeof l?.expires_on === 'string' ? l.expires_on : null,
        expiresAt: typeof l?.expires_at === 'string' ? l.expires_at : null,
      }))
    : Array.isArray(dto.expiring_lots)
      ? dto.expiring_lots.map((l) => ({
          amount:
            typeof l?.remaining === 'number' && Number.isFinite(l.remaining) ? l.remaining : 0,
          expiresOn: lotExpiresOnFromExpiresAt(l?.expires_at),
          expiresAt: lotExpiresAtIso(l?.expires_at),
        }))
      : [];
  return lots
    .filter((l) => l.amount > 0)
    .sort((a, b) => (a.expiresOn ?? '9999-12-31').localeCompare(b.expiresOn ?? '9999-12-31'));
}

/**
 * 效期批次總和 T(docs/74 §1.C;缺欄 = 0)—— 用量% 分子要扣掉它:兌換點是月額度(cap)
 * **之外**的加碼,不扣的話錢包躺著 500 點兌換點會讓用量% 假性歸零。
 */
export function walletExpiringTotal(
  wallet: Pick<WalletEntity, 'expiringLots'> | undefined,
): number {
  return (wallet?.expiringLots ?? []).reduce(
    (sum, l) => sum + (Number.isFinite(l.amount) ? l.amount : 0),
    0,
  );
}

/**
 * `WalletEntityDto` → `WalletEntity`,同時吃 onSnapshot 完整 doc 與 `/me` 輕量殼。
 *
 * @param dto 任一殼(欄位互補,見 `WalletEntityDto` 註解)。
 * @param fallbackGroupId 當 dto 無 `group_id`(`/me` 殼)時補上的 gid。`walletStore`
 *   的 setter 以 `wallet.groupId === gid` 把關 cross-group 寫入,`/me` 殼缺 `group_id`
 *   會被 guard drop;caller(`httpMeService`)以 `current_group_id` 帶入此參數避免被擋。
 */
export function mapWalletEntityDto(dto: WalletEntityDto, fallbackGroupId?: string): WalletEntity {
  // cap:doc 殼 monthly_grant_units 優先,`/me` 殼退 grant_per_period,皆無退 0(試玩)。
  const monthlyGrantUnits = dto.monthly_grant_units ?? dto.grant_per_period ?? 0;
  // available:`/me` 殼後端已給就尊重,doc 殼派生 balance - reserved。
  const available = dto.available ?? dto.balance - dto.reserved;
  const expiringLots = mapExpiringLots(dto);
  // used:`/me` 殼後端已給就尊重;doc 殼派生 max(0, cap - (balance - T))
  // (docs/74 §1.C,2026-09-25 起分子扣效期批次總和 T;沒有批次時 T=0 逐位同舊式)。
  const expiringTotal = walletExpiringTotal({ expiringLots });
  const used = dto.used ?? Math.max(0, monthlyGrantUnits - (dto.balance - expiringTotal));
  return {
    cuid: dto.cuid ?? '',
    groupId: dto.group_id ?? fallbackGroupId ?? '',
    balance: dto.balance,
    reserved: dto.reserved,
    available,
    used,
    monthlyGrantUnits,
    expiringLots,
    lastGrantedPeriod: dto.last_granted_period ?? null,
    nextGrantDueAt: dto.next_grant_due_at ?? null,
    version: dto.version ?? 0,
    createdAt: dto.created_at ?? null,
    updatedAt: dto.updated_at ?? null,
  };
}

/**
 * 用量%(committed 版:已用 + 在途)— 對齊 docs/74 §1.C 拍板算法(2026-09-25 分子扣 T)。
 *
 * `cap = monthlyGrantUnits`(本期天花板;試玩工作區 = 0):
 *   - `cap > 0` → `clamp(round((cap - (available - T)) / cap * 100), 0, 100)`
 *     - 用 `available` 而非 `balance`,進度條才不會在 job 跑完瞬間倒退(committed 版)。
 *     - `T = Σ expiringLots[].amount`(效期批次總和;缺欄 = 0):兌換點是 cap **之外**
 *       的加碼(續期只補「非兌換」部分到 cap),分子不扣 T 的話有兌換點時用量% 會假性
 *       偏低 —— ⚠️ **不可用 `used / (used + available)`**,有批次時分母 ≠ cap。
 *   - `cap <= 0`(試玩工作區 / 無方案)→ 回 `null`,caller 改顯「試玩 N 點」不顯 bar。
 *
 * NaN 防護:cap / available 任一非有限數(壞 doc / 還沒載)一律回 null,不渲染假 bar
 * (T 的非有限數已在 `walletExpiringTotal` 內當 0)。
 *
 * @param wallet 該 group 的 `WalletEntity`(realtime onSnapshot 或 `/me` 派生);
 *   `undefined`(尚未載入)直接回 null。
 * @returns 0–100 整數百分比,或 null(試玩 / 無法計算)。
 */
export function deriveWalletUsagePct(wallet: WalletEntity | undefined): number | null {
  if (!wallet) return null;
  const cap = wallet.monthlyGrantUnits;
  const available = wallet.available;
  if (!Number.isFinite(cap) || !Number.isFinite(available) || cap <= 0) return null;
  const expiringTotal = walletExpiringTotal(wallet);
  const pct = Math.round(((cap - (available - expiringTotal)) / cap) * 100);
  return Math.max(0, Math.min(100, pct));
}
